import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { Protocol } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";

export function RowActions({ cell }: CellContext<Protocol, "actions">) {
	/** Hooks */
	const { refetch } = useDataTableContext();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const asyncModal = useAsyncModal();
	const deleteLoader = useLoading();
	const { citadel } = useCitadel();

	/** Computed */
	// The row is in hand, so the check is bound to it: the rules for a protocol condition on who
	// wrote it and which project it is about, and a category check would answer the wrong question.
	const resource = {
		id: cell.row.original.id,
		orgId: cell.row.original.orgId,
		createdBy: cell.row.original.createdBy,
		projectId: cell.row.original.projectId,
	};
	const canUpdateProtocol = citadel?.can("update", "Protocol", resource) === true;
	const canDeleteProtocol = citadel?.can("delete", "Protocol", resource) === true;

	/** Functions */
	function handleOnNavigateToEdit() {
		navigate(`/protocols/${cell.row.original.id}/edit`);
	}

	async function handleOnDelete() {
		try {
			deleteLoader.start();
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) throw new Error("User cancelled deletion");
			await client.protocols[":protocolId"].$delete({
				param: {
					protocolId: cell.row.original.id,
				},
			});
			await refetch?.();
		} catch (error) {
			console.error("Error deleting protocol:", error);
		} finally {
			deleteLoader.stop();
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={`/protocols/${cell.row.original.id}`}>
				<Menu
					items={createStandardMenuItems({
						onEdit: canUpdateProtocol ? handleOnNavigateToEdit : undefined,
						onDelete: canDeleteProtocol ? handleOnDelete : undefined,
					})}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="protocol-row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("confirmation.confirm_deletion")}
				/>
			)}
		</React.Fragment>
	);
}
